/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type {
  MovementsHomeRequest,
  MovementsHomeResponse,
  MovementsHomeResponseV4,
  RecommendationApiItem,
  RecommendationApiResponse,
  RecommendationRequestPayload,
} from './types';

declare const __DEV__: boolean;

const ensureBearerToken = (token: string): string => {
  const trimmed = token.trim();
  if (trimmed.toLowerCase().startsWith('bearer ')) return trimmed;
  return `Bearer ${trimmed}`;
};

export type RecommendationApiEnvironment = 'stg' | 'prd';

export class RecommendationApiService {
  private readonly client: ApiClient;

  constructor(environment: RecommendationApiEnvironment = 'stg') {
    this.client = CoreNetworking({
      baseUrl: this.getBaseUrl(environment),
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (config) => {
        if (__DEV__) {
          console.log('🚀 [RecommendationApi] REQUEST:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            headers: config.headers,
            data: config.data,
          });
        }
        return config;
      },
      (error) => {
        if (__DEV__) {
          console.error('❌ [RecommendationApi] REQUEST ERROR:', error);
        }
        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use(
      (response) => {
        if (__DEV__) {
          console.log('✅ [RecommendationApi] RESPONSE:', {
            status: response.status,
            statusText: response.statusText,
            url: response.config.url,
            data: response.data,
          });
        }
        return response;
      },
      (error) => {
        if (__DEV__) {
          console.error('❌ [RecommendationApi] RESPONSE ERROR:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            data: error.response?.data,
          });
        }
        return Promise.reject(error);
      },
    );
  }

  private getBaseUrl(environment: RecommendationApiEnvironment): string {
    return environment === 'stg'
      ? 'https://recommend.stg-apiairasia.com'
      : 'https://recommend.apiairasia.com';
  }

  /**
   * Fetch social feed movements home data.
   *
   * Endpoint: `POST /v1/movements/home`
   */
  async fetchMovementsHome(params: {
    accessToken: string;
    locale: string;
    market: string;
    platform: string;
    appVersion: string;
    channelHash: string;
    maxPostsPerSection?: number | null;
    maxSections?: number | null;
    maxChips?: number | null;
    searchHistory?: MovementsHomeRequest['searchHistory'];
    location?: MovementsHomeRequest['location'];
  }): Promise<MovementsHomeResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: ensureBearerToken(params.accessToken),
      'Accept-Language': params.locale,
      'X-Platform': params.platform,
      'X-Market': params.market,
      'X-App-Version': params.appVersion,
      'X-Channel-Hash': params.channelHash,
    };

    const body: MovementsHomeRequest = {
      maxPostsPerSection: params.maxPostsPerSection ?? undefined,
      maxSections: params.maxSections ?? undefined,
      maxChips: params.maxChips ?? undefined,
      searchHistory: params.searchHistory,
      location: params.location,
    };

    const response = await this.client.post<MovementsHomeResponse>(
      '/v1/movements/home',
      body,
      { headers },
    );
    return response.data;
  }

  async fetchMovementsHomeV4(params: {
    accessToken: string;
    userId?: string;
    currency?: string;
    locale: string;
    market: string;
    platform: string;
    appVersion: string;
    channel?: string;
    channelHash?: string;
    searchHistory?: MovementsHomeRequest['searchHistory'];
    location?: MovementsHomeRequest['location'];
    page?: number;
    pageSize?: number;
    /** React Query abort signal — cancels the HTTP request when the query is
     * superseded, so an orphaned response can't log a misleading success. */
    signal?: AbortSignal;
  }): Promise<MovementsHomeResponseV4> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: ensureBearerToken(params.accessToken),
      channel: params.channel || '',
      channelHash: params.channelHash || '',
    };

    const body = {
      location: params.location,
      locale: params.locale,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      userId: params.userId,
      currency: params.currency,
      searchHistory: params.searchHistory,
    };

    const response = await this.client.post<MovementsHomeResponseV4>(
      '/v4/movements/home/',
      body,
      { headers, signal: params.signal },
    );

    return response.data;
  }

  async fetchRecommendations(params: {
    accessToken: string;
    clientId: string;
    apiKey: string;
    /** Internal ("Allstar") user flag. Omitted from the request when `undefined`
     * so an unresolved profile isn't asserted as `false`. */
    isAllStar?: boolean;
    payload: RecommendationRequestPayload;
  }): Promise<RecommendationApiResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: ensureBearerToken(params.accessToken),
      'sso-client-id': params.clientId,
      'sso-api-key': params.apiKey,
    };

    if (params.isAllStar !== undefined) {
      headers['X-Allstar'] = String(params.isAllStar);
    }

    const response = await this.client.post<RecommendationApiResponse>(
      '/v4/recommendations/',
      {
        ...params.payload,
        page: params.payload.page ?? 1,
        pageSize: params.payload.pageSize ?? 20,
      },
      { headers },
    );

    const data = response.data;
    data.data =
      data.data?.map((item: RecommendationApiItem) => ({
        ...item,
        lob: item.lob?.toLowerCase(),
      })) ?? [];
    data.totalRecords = data.totalRecords ?? 0;
    return data;
  }

  async fetchForYou(params: {
    accessToken: string;
    clientId: string;
    apiKey: string;
    isAllStar?: boolean;
    payload: Omit<RecommendationRequestPayload, 'focus' | 'lob'>;
  }): Promise<RecommendationApiResponse> {
    return this.fetchRecommendations({
      accessToken: params.accessToken,
      clientId: params.clientId,
      apiKey: params.apiKey,
      isAllStar: params.isAllStar,
      payload: {
        ...params.payload,
        focus: ['PERSONALIZE'],
        lob: [
          'HOTEL',
          'COMMUNITY',
          'FOOD',
          'RIDE',
          'FLIGHT',
          'SNAP',
          'SHOP',
          'ANCILLARY',
          'LIFE',
          'PROMOTIONS',
          'REWARDS',
          'AD_V3',
        ],
      },
    });
  }

  async fetchPromotions(params: {
    accessToken: string;
    clientId: string;
    apiKey: string;
    isAllStar?: boolean;
    payload: Omit<RecommendationRequestPayload, 'focus' | 'lob'>;
  }): Promise<RecommendationApiResponse> {
    return this.fetchRecommendations({
      accessToken: params.accessToken,
      clientId: params.clientId,
      apiKey: params.apiKey,
      isAllStar: params.isAllStar,
      payload: {
        ...params.payload,
        focus: ['PROMOTE'],
        lob: ['PROMOTIONS'],
      },
    });
  }
}
